"use client";

import { useState, useEffect } from "react";
import classNames from "classnames";
import Link from "next/link";
import Image from "next/image";
import { HeaderProps } from "@/types/component.types";
import Button from "@/components/ui/Button";
import { useRouter, usePathname } from "next/navigation";
import ThemeToggle from "@/components/utils/ThemeToggle";
import { motion } from "framer-motion";

export default function Header({
  blok,
  lang = "de"
}: {
  blok?: HeaderProps,
  lang?: 'en' | 'de'
}) {

  const router = useRouter();
  const pathname = usePathname();
  const [currentLang, setCurrentLang] = useState(lang);
  
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY <= 20) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleLang = () => {
    const newLang = currentLang === "de" ? "en" : "de";
    setCurrentLang(newLang);
    const segments = pathname.split("/");

    if (segments.length > 1) {
      segments[1] = newLang;
      router.push(segments.join("/"));
    } else {
      router.push(`/${newLang}`);
    }
  };

  const isHomePage = ["/", "/de", "/en"].includes(pathname);

  return (
    <header 
      className={classNames(
        "fixed top-0 z-50 w-full py-4 md:py-8 transition-transform duration-300 ease-in-out bg-transparent",
        isVisible ? "translate-y-0" : "-translate-y-full"
      )}
    >
      <div className="container flex items-center justify-between">
        
        <div className="shrink-0">
          <Link 
            href={`/${currentLang}`} 
            draggable={false}
            className="group block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green"
            aria-label="Go to homepage"
          >
            {blok?.logo?.[0]?.src?.filename && (
              <motion.div
                drag
                dragConstraints={{ top: 0, left: 0, right: 0, bottom: 0 }}
                dragElastic={0.8}
                dragTransition={{ bounceStiffness: 600, bounceDamping: 15 }}
                whileDrag={{ scale: 1.2, zIndex: 50, cursor: "grabbing" }}
                className="relative z-50 custom-cursor-hover xl:hover:cursor-none"
              >
                <Image
                  src={blok.logo[0].src.filename}
                  alt={blok.logo[0].alt || "Portfolio Logo"}
                  width={50}
                  height={50}
                  loading="eager"
                  draggable={false}
                  className="h-15 w-auto object-contain group-hover:scale-110 group-hover:opacity-70 duration-300"
                />
              </motion.div>
            )}
          </Link>
        </div>

        {isHomePage && (
          <nav 
            className="hidden md:flex items-center gap-x-8 xl:gap-x-12 absolute left-1/2 -translate-x-1/2"
            aria-label="Main Navigation"
          >
            {blok?.nav && blok.nav.map((item: any) => (
              <Button 
                key={item._uid} 
                variant="tertiary"
                size="large"
                href={item.link?.cached_url || item.link?.url || "#"}
              >
                {item.label}
              </Button>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-6">
          <button 
            className="text-black dark:text-beige text-[18px] md:text-[20px] font-funnel-bold hover:text-green transition-colors uppercase tracking-wider p-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green"
            onClick={toggleLang}
            type="button"
            aria-label={currentLang === 'en' ? 'EN - Switch to German' : 'DE - Auf Englisch wechseln'}
          >
            {currentLang}
          </button>

          <ThemeToggle currentLang={currentLang} />
        </div>

      </div>
    </header>
  )
}